// This file is for temporary use while we're mixing generated BE types with
// legacy FE types. Ideally we should just use the BE enum types in the FE.

import {
  type DatapointPolymorph,
  ISSB_MAJOR_CODE,
  type Reference,
  type Standard,
} from '@netpurpose/types'
import { valueIsDefined } from '@netpurpose/utils'
import { pickBy } from 'lodash'
import type { ReportingStandardMajorDetails } from '../../../generated/facts'
import {
  type Reference as BackendReference,
  type ReportingStandard as BackendStandard,
  type CreateReference as CreateBackendReference,
  type Datapoint,
  DatapointTypes,
  type MacroFactor,
  ReportingStandardClasses,
  type TechnologyCost,
} from '../../../generated/modelling'
import { type BackendSource, parseSource } from '../../../models'
import { snakeToCamelKeys } from '../../../utils'
import { mapDatapointStatusBEToFE, mapDatapointTypeBEToFE } from './enumMappers'

export const formatStandard = (standard: Standard): BackendStandard => ({
  reporting_standard_id: standard.id,
  standard_class: standard.standardClass as BackendStandard['standard_class'],
  ...pickBy(
    {
      major_code:
        standard.standardClass === ('ISSB' satisfies BackendStandard['standard_class'])
          ? ''
          : standard.majorCode,
      full_code: standard.fullCode,
      description: standard.description,
      link: standard.link,
      colour: standard.colour,
    },
    valueIsDefined,
  ),
})

const isBackendStandard = (
  standard: BackendStandard | ReportingStandardMajorDetails,
): standard is BackendStandard => !!(standard as BackendStandard).reporting_standard_id

export const parseReportingStandard = (
  standard: BackendStandard | ReportingStandardMajorDetails,
): Standard => ({
  id: isBackendStandard(standard) ? standard.reporting_standard_id : -1,
  standardClass: standard.standard_class,
  majorCode:
    standard.standard_class === ReportingStandardClasses.ISSB
      ? ISSB_MAJOR_CODE
      : standard.major_code || undefined,
  fullCode: isBackendStandard(standard) ? standard.full_code || undefined : undefined,
  description: isBackendStandard(standard) ? standard.description || undefined : undefined,
  link: standard.link || undefined,
  colour: standard.colour || undefined,
})

const parseReference = (
  reference: BackendReference | CreateBackendReference,
): Partial<Reference> => ({
  ...('reference_id' in reference ? { id: reference.reference_id } : {}),
  ...(reference.source
    ? {
        source: {
          ...parseSource({
            ...(reference.source as BackendSource),
            source_id: reference.source_id,
            original_url: reference.source.original_url || '',
          }),
        },
      }
    : {}),
  ...(reference.page_num ? { pageNumber: reference.page_num } : {}),
})

const isBackendTechnologyCost = (
  datapoint: MacroFactor | TechnologyCost | Datapoint,
): datapoint is TechnologyCost => datapoint.type === DatapointTypes.TECHNOLOGY_COST

export const parseDatapointPolymorph = (
  datapoint: MacroFactor | TechnologyCost | Datapoint,
): DatapointPolymorph => {
  const { datapointId: _datapointId, ...rest } = snakeToCamelKeys(datapoint)

  return {
    ...rest,
    id: datapoint.datapoint_id || '',
    type: mapDatapointTypeBEToFE(datapoint.type),
    value: datapoint.value,
    ...(isBackendTechnologyCost(datapoint) && datapoint.technology
      ? {
          technology: {
            id: datapoint.technology.technology_id,
            name: datapoint.technology.name,
            parentId: datapoint.technology.parent_id ?? null,
          },
        }
      : {}),
    // raw should actually always be defined, but if not probably more likely to be true.
    raw: datapoint.raw ?? true,
    status: mapDatapointStatusBEToFE(datapoint.status || undefined),
    reference: datapoint.reference ? parseReference(datapoint.reference) : undefined,
    // The equals sign gets stripped out before sending it to the BE, so when we get it back
    // we need to reinsert it so that when it gets submitted again it doesn't remove
    // the first character of the formula.
    formula: datapoint.formula ? `=${datapoint.formula}` : undefined,
    reportedValue: datapoint.reported_value ?? undefined,
    reportedUnitId: datapoint.reported_unit_id ?? undefined,
    originalWorkbookId: datapoint.original_workbook_id ?? undefined,
    reportingEndDate: datapoint.reporting_end_date || null,
  }
}
